import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import axios from 'axios';

import { router } from '@/router';
import { LoginPage } from '@/pages/LoginPage';
import { PasswortAendernPage } from '@/pages/PasswortAendernPage';
import { useAdminAuth, istBetreiber } from '@/store/useAdminAuth';
import { ConfirmDialogProvider } from '@/components/common/ConfirmDialog';
import '@fontsource-variable/inter';
import '@/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5_000, retry: 1, refetchOnWindowFocus: false },
  },
});

/**
 * Entscheidet zwischen Anmeldemaske und Konsole.
 *
 * Beim Start wird EINMAL versucht, aus dem Refresh-Cookie eine Sitzung
 * herzustellen – sonst müsste man sich nach jedem Neuladen neu anmelden,
 * obwohl das Cookie noch gilt.
 */
function Wurzel() {
  const { user, wirdGeladen, anmelden, setzeGeladen } = useAdminAuth();

  useEffect(() => {
    let abgebrochen = false;
    void (async () => {
      try {
        const { data } = await axios.post('/api/auth/refresh', null, { withCredentials: true });
        if (abgebrochen) return;

        // Ein Cookie eines Kontos ohne Konsolenzugang wird nicht zur Sitzung,
        // sondern abgeräumt – sonst bliebe es liegen und würde bei jedem
        // Laden erneut eingelöst.
        if (!istBetreiber(data.user)) {
          await axios
            .post('/api/auth/logout', {}, {
              withCredentials: true,
              headers: { 'X-CSRF-Token': data.csrfToken },
            })
            .catch(() => {});
          setzeGeladen(false);
          return;
        }

        anmelden(data.user, data.accessToken, data.csrfToken);
      } catch {
        if (!abgebrochen) setzeGeladen(false);
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [anmelden, setzeGeladen]);

  if (wirdGeladen) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900">
        <p className="text-sm text-slate-400">Sitzung wird geprüft…</p>
      </div>
    );
  }

  // Auch ein gültig angemeldetes Konto ohne Betreiberrolle sieht hier nichts.
  // Der Server weist es ohnehin ab; das hier erspart die leere Oberfläche.
  if (!user || !istBetreiber(user)) return <LoginPage />;

  // Vor dem Router, nicht darin: mit gesetztem Flag antwortet der Server auf
  // jede andere Route mit 403 – ein Dashboard voller Fehlermeldungen wäre die
  // Alternative.
  if (user.mustChangePassword) return <PasswortAendernPage />;

  return <RouterProvider router={router} />;
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root-Element #root nicht im DOM gefunden.');

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Die übernommenen Seiten fragen über useConfirm() nach, bevor sie
          löschen – ohne diesen Provider stürzen sie ab. */}
      <ConfirmDialogProvider>
        <Wurzel />
      </ConfirmDialogProvider>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: { fontSize: '14px', borderRadius: '8px' },
          error: { duration: 6000 },
        }}
      />
    </QueryClientProvider>
  </StrictMode>,
);
